import {
  AddressSuggestion,
  AutocompleteAddress,
} from 'components/Form/AutocompleteAddress';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import {
  SmartTextCreateRequest,
  SmartTextFormatResponse,
  SmartTextOptionGroupResponse,
  SmartTextOptionResponse,
  SmartTextResponse,
} from 'api/core';
import { LabelWithHelperText } from 'components/Form/LabelWithHelperText';
import { EntitySelectMultiple } from 'components/Select/EntitySelectMultiple';
import { useCreateSmartText } from 'api/useSmartTextApi';
import { formatNumber } from 'utils/format/field';
import { useGetOrCreateGhostCase } from 'api/useCasesApi';
import { AnimatedIcon } from 'components/Icon/AnimatedIcon';

interface SmartTextFormProps {
  allSmartTextFormats: SmartTextFormatResponse[];
  allSmartTextOptionGroups: SmartTextOptionGroupResponse[];
  onSuccess?: (id: string) => void;
  onCancel?: () => void;
  copyOf?: SmartTextResponse;
  inDialog?: boolean;
}

export const SmartTextCreateForm = ({
  allSmartTextFormats,
  allSmartTextOptionGroups,
  onSuccess,
  onCancel,
  copyOf,
  inDialog = false,
}: SmartTextFormProps) => {
  const MIN_SELLING_POINTS = 3;
  const MAX_SELLING_POINTS = 5;
  const MAX_FORMATS = 10;
  const STEPS = [
    EStep.Address,
    EStep.SellingPoints,
    EStep.Preferences,
    EStep.Formats,
  ];

  const { mutateAsync: createSmartTextAsync, isPending: isPendingCreate } =
    useCreateSmartText();

  const { mutateAsync: getOrCreateGhostCaseAsync } = useGetOrCreateGhostCase();

  const onAddressConfirmed = async () => {
    await getOrCreateGhostCaseAsync({
      getOrCreateCaseAsGhostRequest: {
        address: watch('address'),
        dawaAddressId: selectedSuggestion?.data.id,
      },
    });
  };

  const [selectedSuggestion, setSelectedSuggestion] =
    useState<AddressSuggestion>();

  const [currentStep, setCurrentStep] = useState(STEPS[0]);

  const [sellingPoints, setSellingPoints] = useState<string[]>(
    copyOf?.sellingPoints?.length
      ? copyOf.sellingPoints.map((sp) => sp.value)
      : new Array(MIN_SELLING_POINTS).fill('')
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { isValid },
  } = useForm<{
    address: string;
    optionsGroups: {
      optionGroupId: string;
      options: string[];
    }[];
    outputFormats: OutputFormat[];
  }>({
    defaultValues: {
      address: copyOf?._case?.address ?? '',
      outputFormats: allSmartTextFormats.map((format, i) => ({
        format,
        enabled: !copyOf
          ? i < MAX_FORMATS
          : copyOf.results.some((r) => r.format.id === format.id),
        requestedLength: !copyOf
          ? format.defaultLength
          : (copyOf.results.find((r) => r.format.id === format.id)
              ?.requestedLength ?? format.defaultLength),
      })),
      optionsGroups: allSmartTextOptionGroups.map((group) => ({
        optionGroupId: group.id,
        options: !copyOf
          ? []
          : group.options
              .filter((o) => copyOf.options.some((opt) => opt === o.id))
              .map((o) => o.id),
      })),
    },
  });

  const onSubmit = handleSubmit(async () => {
    if (!selectedSuggestion || !selectedSuggestion.data.id) return;
    if (!isValid) return;

    const request: SmartTextCreateRequest = {
      additionalContext: '',
      formatIds: watch('outputFormats')
        .filter((of) => of.enabled)
        .map((of) => of.format.id),
      formatLengths: watch('outputFormats')
        .filter((of) => of.enabled)
        .map((of) => of.requestedLength),
      options: watch('optionsGroups')
        ?.map((group) => group.options)
        .flat(),
      sellingPoints: sellingPoints.filter((sp) => sp.trim()),
      address: selectedSuggestion.forslagstekst,
      dawaAddressId: selectedSuggestion.data.id,
    };

    const textCollection = await createSmartTextAsync({
      smartTextCreateRequest: request,
    });

    onSuccess?.(textCollection.id);
  });

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <div className="flex flex-col pt-2 gap-4">
        <ul className="steps steps-horizontal w-full">
          {STEPS.map((s, index) => (
            <li
              onClick={() => setCurrentStep(Math.min(currentStep, s))}
              id={`step-${index + 1}`}
              key={index}
              className={twMerge(
                'step !min-w-32 pb-4 md:pb-0',
                s == currentStep && 'font-bold',
                s <= currentStep &&
                  'cursor-pointer step-primary after:!text-white hover:font-bold hover:step-info'
              )}
            >
              <span>{translateStep(s)}</span>
            </li>
          ))}
        </ul>
        <div className="divider my-0"></div>
        {currentStep === EStep.Address ? (
          <>
            <AutocompleteAddress
              label={
                <p>
                  Adresse <span className="text-red-500">*</span>
                </p>
              }
              suggestAddresses={true}
              registration={() => register('address', { required: true })}
              formWatch={watch('address') || ''}
              formSetValue={(value: string) => setValue('address', value)}
              onSuggestionSelectedFn={setSelectedSuggestion}
            />
          </>
        ) : currentStep === EStep.SellingPoints ? (
          <>
            {sellingPoints.map((_, index) => (
              <div className="form-control w-full" key={index}>
                <label className="label" htmlFor={`selling-point-${index}`}>
                  <p>
                    Købsargument {index + 1}
                    {index < MIN_SELLING_POINTS ? (
                      <span className="text-red-500"> *</span>
                    ) : null}
                  </p>
                </label>
                <div className="join w-full">
                  <input
                    key={index}
                    id={`selling-point-${index}`}
                    data-testid="smart-text-selling-point-input"
                    type="text"
                    className="input input-bordered join-item w-full"
                    placeholder={`Købsargument ${index + 1}`}
                    value={sellingPoints[index]}
                    onChange={(e) => {
                      const newSellingPoints = [...sellingPoints];
                      newSellingPoints[index] = e.target.value;
                      setSellingPoints(newSellingPoints);
                    }}
                  />
                  {index >= MIN_SELLING_POINTS && (
                    <button
                      type="button"
                      className="btn btn-neutral join-item"
                      data-testid={`smart-text-delete-selling-point-${index}`}
                      onClick={() => {
                        const newSellingPoints = sellingPoints.filter(
                          (_, i) => i !== index
                        );
                        setSellingPoints(newSellingPoints);
                      }}
                    >
                      <AnimatedIcon
                        icon="trash-icon"
                        className="h-8 md:h-6 w-8 md:w-6"
                      />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {sellingPoints.length < MAX_SELLING_POINTS ? (
              <button
                data-testid="smart-text-add-selling-point"
                type="button"
                className="btn btn-sm btn-outline w-fit"
                disabled={
                  sellingPoints.length >= MAX_SELLING_POINTS || isPendingCreate
                }
                onClick={() => {
                  setSellingPoints([...sellingPoints, '']);
                }}
              >
                Tilføj endnu et købsargument
              </button>
            ) : null}
          </>
        ) : currentStep === EStep.Preferences ? (
          <>
            {allSmartTextOptionGroups.map((optionGroup) => (
              <div key={optionGroup.id} className="form-control">
                <LabelWithHelperText
                  label={optionGroup.name}
                  inDialog={true}
                  htmlFor={`option-group-${optionGroup.id}`}
                  helperText={optionGroup.description}
                />
                <EntitySelectMultiple<SmartTextOptionResponse>
                  id={`option-group-${optionGroup.id}`}
                  data={optionGroup.options}
                  renderFormat={(option) => (
                    <span data-testid={`smart-text-option-${option.id}`}>
                      {option.name}
                    </span>
                  )}
                  disabled={isPendingCreate}
                  inDialog={true}
                  initialValue={
                    !copyOf
                      ? []
                      : optionGroup.options.filter((o) =>
                          copyOf.options.some((opt) => opt === o.id)
                        )
                  }
                  searchPropertyKey="name"
                  maxSelections={optionGroup.maxNumberOfSelectedOptions}
                  onSelect={(options) => {
                    const currentOptionsGroups = watch('optionsGroups') || [];
                    const group = currentOptionsGroups.find(
                      (g) => g.optionGroupId === optionGroup.id
                    );
                    if (group) {
                      group.options = options?.map((o) => o.id) || [];
                    } else {
                      currentOptionsGroups.push({
                        optionGroupId: optionGroup.id,
                        options: options?.map((o) => o.id) || [],
                      });
                    }

                    setValue('optionsGroups', currentOptionsGroups, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });

                    if (
                      optionGroup.minNumberOfSelectedOptions &&
                      (options?.length || 0) <
                        optionGroup.minNumberOfSelectedOptions
                    ) {
                      setError(
                        'optionsGroups',
                        {
                          type: 'manual',
                          message: `Du skal vælge mindst ${optionGroup.minNumberOfSelectedOptions} muligheder i ${optionGroup.name}`,
                        },
                        { shouldFocus: true }
                      );
                    }
                  }}
                />
                {optionGroup.minNumberOfSelectedOptions &&
                (watch('optionsGroups').find(
                  (e) => e.optionGroupId === optionGroup.id
                )?.options.length || 0) <
                  optionGroup.minNumberOfSelectedOptions ? (
                  <p className="text-sm text-gray-500">
                    Du skal vælge mindst{' '}
                    {optionGroup.minNumberOfSelectedOptions} muligheder
                  </p>
                ) : null}
              </div>
            ))}

            <input
              {...register('optionsGroups', {
                validate: () => {
                  return (
                    allSmartTextOptionGroups.every((group) => {
                      const groupSelection = watch('optionsGroups').find(
                        (g) => g.optionGroupId === group.id
                      );
                      const selectedCount =
                        groupSelection?.options?.length || 0;
                      return (
                        !group.minNumberOfSelectedOptions ||
                        selectedCount >= group.minNumberOfSelectedOptions
                      );
                    }) || 'Nogle grupper mangler valgte muligheder'
                  );
                },
              })}
              type="text"
              className="hidden"
            />
          </>
        ) : currentStep === EStep.Formats ? (
          <>
            <div className="form-control">
              {/* Header row */}
              <div className="flex items-center justify-between font-semibold mb-2 px-1 text-sm text-gray-500">
                <div className="w-32">Navn</div>
                <div className="mx-4">Valgt</div>
                <div className="w-28 text-right">
                  <LabelWithHelperText
                    helperText="Antal tegn i teksten. Dette er kun en vejledende længde, og den endelige tekst kan variere i længde."
                    label="Antal tegn"
                    inDialog={inDialog}
                  />
                </div>
              </div>

              {/* Rows */}
              {watch('outputFormats').map((of, index) => (
                <div
                  key={index}
                  className="form-control flex items-center justify-between mb-2"
                  style={{ flexDirection: 'row' }}
                >
                  <div className="text-left w-32">
                    <LabelWithHelperText
                      htmlFor={'smart-text-format-' + of.format.id}
                      inDialog={true}
                      position="right"
                      label={of.format.name}
                      helperText={
                        of.format.description ||
                        `Til ${of.format.name.toLocaleLowerCase()} kan du vælge mellem ${formatNumber(of.format.minLength)} og ${formatNumber(of.format.maxLength)} tegn. Bemærk, at dette er for at guide Smart Text til at skrive teksten, og at den endelige tekst kan variere i længde.`
                      }
                    />
                  </div>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary mx-4"
                    id={'smart-text-format-' + of.format.id}
                    checked={of.enabled}
                    disabled={
                      isPendingCreate ||
                      (!of.enabled &&
                        watch('outputFormats').filter((of) => of.enabled)
                          .length >= MAX_FORMATS)
                    }
                    title={
                      !of.enabled &&
                      watch('outputFormats').filter((of) => of.enabled)
                        .length >= MAX_FORMATS
                        ? `Du kan kun vælge ${MAX_FORMATS} formater`
                        : undefined
                    }
                    onChange={(e) => {
                      const newOutputFormats = [...watch('outputFormats')];
                      newOutputFormats[index].enabled = e.target.checked;
                      setValue('outputFormats', newOutputFormats);
                    }}
                  />
                  <input
                    type="number"
                    className={twMerge(
                      'input input-bordered w-28 text-right',
                      !of.enabled && 'disabled'
                    )}
                    value={of.requestedLength}
                    disabled={!of.enabled || isPendingCreate}
                    onChange={(e) => {
                      const newOutputFormats = [...watch('outputFormats')];
                      newOutputFormats[index].requestedLength = parseInt(
                        e.target.value
                      );
                      setValue('outputFormats', newOutputFormats);
                    }}
                    onBlur={(e) => {
                      let newValue = parseInt(e.target.value);
                      if (isNaN(newValue)) {
                        newValue = of.format.minLength;
                      }
                      if (newValue < of.format.minLength) {
                        newValue = of.format.minLength;
                      }
                      if (newValue > of.format.maxLength) {
                        newValue = of.format.maxLength;
                      }
                      const newOutputFormats = [...watch('outputFormats')];
                      newOutputFormats[index].requestedLength = newValue;
                      setValue('outputFormats', newOutputFormats);
                    }}
                    min={of.format.minLength}
                    max={of.format.maxLength}
                    readOnly={of.format.minLength === of.format.maxLength}
                  />
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>

      <div className="flex justify-center space-x-4 pt-4">
        {currentStep !== STEPS[STEPS.length - 1] ? (
          <button
            data-testid="smart-text-next-step"
            className="btn btn-primary"
            disabled={
              isPendingCreate ||
              (currentStep === EStep.Address && !selectedSuggestion) ||
              (currentStep === EStep.SellingPoints &&
                sellingPoints.filter((sp) => sp?.trim()).length <
                  MIN_SELLING_POINTS)
            }
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (currentStep === EStep.Address) {
                onAddressConfirmed();
              }
              setCurrentStep((e) => e + 1);
            }}
          >
            Fortsæt
          </button>
        ) : (
          <button
            data-testid="smart-text-submit"
            type="submit"
            className="btn btn-primary"
            disabled={
              isPendingCreate ||
              !isValid ||
              watch('outputFormats').every((of) => !of.enabled)
            }
          >
            Lav boligtekst
          </button>
        )}

        {onCancel && !isPendingCreate ? (
          <button type="button" className="btn" onClick={onCancel}>
            Annuller
          </button>
        ) : null}
      </div>
    </form>
  );
};

enum EStep {
  Address,
  SellingPoints,
  Preferences,
  Formats,
}

const translateStep = (step: EStep) => {
  switch (step) {
    case EStep.Address:
      return 'Adresse';
    case EStep.SellingPoints:
      return 'Købsargumenter';
    case EStep.Preferences:
      return 'Præferencer';
    case EStep.Formats:
      return 'Formater';
    default:
      return 'Ukendt';
  }
};

interface OutputFormat {
  format: SmartTextFormatResponse;
  enabled: boolean;
  requestedLength: number;
}
